import { AUTH_URL } from 'src/configs';
import request from 'src/request';
import { AdminRoleRecord, PERMISSION_CATALOG, PermissionCatalogItem, SEEDED_ROLES } from 'src/utils/rbacCatalog';

export type PermissionRecord = PermissionCatalogItem & {
  _id: string;
};
import { normalizeAdminUser } from './adminUserUtils';

export type AdminUserRecord = {
  _id: string;
  id?: string;
  account_id: string;
  role_id: string;
  status: string;
  is_super_admin?: boolean;
  created_at?: string;
  name?: string;
  email?: string;
  role?: {
    id?: string;
    _id?: string;
    key?: string;
    name?: string;
  };
  user?: {
    email?: string;
    name?: string;
    id?: string;
  };
};

const normalizeRole = (role: any): AdminRoleRecord => ({
  _id: role._id ?? role.id ?? role.key,
  key: role.key,
  name: role.name,
  description: role.description ?? '',
  permissions: role.permissions ?? [],
  status: role.status ?? 'active',
});

const normalizeAdmin = (admin: any): AdminUserRecord => normalizeAdminUser(admin);

/** UI-only grouping when API does not store module (derived from permission key). */
export function deriveModuleFromPermissionKey(key: string): string {
  if (key === 'admin:all') return 'Admin';
  const segment = key.split(':')[1];
  if (!segment) return 'General';
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

const normalizePermission = (perm: any): PermissionRecord => {
  const key = perm.key ?? '';
  return {
    _id: String(perm.permission_id ?? perm._id ?? perm.id ?? key),
    key,
    label: perm.name ?? perm.label ?? key,
    description: perm.description ?? '',
    module: perm.module ?? deriveModuleFromPermissionKey(key),
  };
};

const catalogToPermissionRecords = (): PermissionRecord[] =>
  PERMISSION_CATALOG.map((p) => ({ ...p, _id: p.key }));

export function toPermissionCatalogItems(perms: PermissionRecord[]): PermissionCatalogItem[] {
  return perms.map(({ key, label, description, module }) => ({ key, label, description, module }));
}

type ListMeta = {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
};

const parseListMeta = (data: Record<string, unknown>, itemCount: number): ListMeta => {
  const meta = (data.meta ?? {}) as Record<string, unknown>;

  const totalCount = Number(
    meta.total_count ?? meta.totalCount ?? data.total_count ?? data.totalCount ?? itemCount,
  );
  const totalPages = Number(
    meta.page_count ?? meta.pageCount ?? data.total_pages ?? data.totalPages ?? 1,
  );
  const page = Number(meta.page ?? data.page ?? 1);
  const limit = Number(meta.limit ?? data.limit ?? itemCount);

  return {
    page: Number.isFinite(page) ? page : 1,
    limit: Number.isFinite(limit) ? limit : itemCount,
    totalCount: Number.isFinite(totalCount) ? totalCount : itemCount,
    totalPages: Math.max(1, Number.isFinite(totalPages) ? totalPages : 1),
  };
};

export async function fetchAdminUsers(page = 1, limit = 10) {
  const response = await request.get(`${AUTH_URL}/v1/admin/admins`, {
    params: { page, limit },
  });
  const data = response.data;
  const admins = (data.admins ?? data.data ?? data ?? []).map(normalizeAdmin);
  const meta = parseListMeta(data, admins.length);

  return {
    admins,
    totalCount: meta.totalCount,
    totalPages: meta.totalPages,
    page: meta.page,
    limit: meta.limit,
  };
}

export type AdminUserCreatePayload = {
  email: string;
  password: string;
  role_id: string;
  custom_permissions?: string[];
};

export type AdminUserUpdatePayload = {
  role_id: string;
  status: string;
};

export async function createAdminUser(payload: AdminUserCreatePayload) {
  const response = await request.post(`${AUTH_URL}/v1/admin/admins`, {
    email: payload.email,
    password: payload.password,
    role_id: payload.role_id,
    custom_permissions: payload.custom_permissions ?? [],
  });
  return normalizeAdmin(response.data.admin ?? response.data);
}

export async function updateAdminUser(adminId: string, payload: AdminUserUpdatePayload) {
  const response = await request.patch(`${AUTH_URL}/v1/admin/admins/${adminId}`, {
    role_id: payload.role_id,
    status: payload.status,
  });
  return normalizeAdmin(response.data.admin ?? response.data);
}

export async function fetchRoles(page?: number, limit?: number) {
  const paginate = page !== undefined && limit !== undefined;

  try {
    const response = await request.get(`${AUTH_URL}/v1/admin/roles`, {
      ...(paginate ? { params: { page, limit } } : {}),
    });
    const data = response.data;
    const roles = data.roles ?? data.data ?? data ?? [];
    if (Array.isArray(roles) && (roles.length || paginate)) {
      const normalized = roles.map(normalizeRole);
      const meta = paginate
        ? parseListMeta(data, normalized.length)
        : {
            page: 1,
            limit: normalized.length,
            totalCount: normalized.length,
            totalPages: 1,
          };

      return {
        roles: normalized,
        fromApi: true,
        totalCount: meta.totalCount,
        totalPages: meta.totalPages,
        page: meta.page,
        limit: meta.limit,
      };
    }
  } catch {
    // Spec: roles listing endpoint may not exist yet — use seeded catalog.
  }

  const seeded = SEEDED_ROLES.map((role) => ({ ...role, _id: role._id ?? role.key }));

  return {
    roles: seeded,
    fromApi: false,
    totalCount: seeded.length,
    totalPages: 1,
    page: 1,
    limit: seeded.length,
  };
}

export async function createRole(payload: {
  key: string;
  name: string;
  description?: string;
  permissions: string[];
  status?: string;
}) {
  const response = await request.post(`${AUTH_URL}/v1/admin/roles`, payload);
  return normalizeRole(response.data.role ?? response.data);
}

export type RoleUpdatePayload = {
  key: string;
  name: string;
  description: string;
  permissions: string[];
  status: string;
};

export async function updateRole(roleId: string, payload: RoleUpdatePayload) {
  const response = await request.patch(`${AUTH_URL}/v1/admin/roles/${roleId}`, payload);
  return normalizeRole(response.data.role ?? response.data);
}

export async function fetchPermissions(): Promise<{ permissions: PermissionRecord[]; fromApi: boolean }> {
  try {
    const response = await request.get(`${AUTH_URL}/v1/admin/permissions`);
    const list = response.data.permissions ?? response.data.data ?? response.data ?? [];
    if (Array.isArray(list)) {
      return { permissions: list.map(normalizePermission), fromApi: true };
    }
  } catch {
    // Fall back to static catalog when registry endpoint is unavailable.
  }

  return { permissions: catalogToPermissionRecords(), fromApi: false };
}

export async function createPermission(payload: { key: string; name: string; description?: string }) {
  const response = await request.post(`${AUTH_URL}/v1/admin/permissions`, {
    key: payload.key,
    name: payload.name,
    description: payload.description ?? '',
  });
  return normalizePermission(response.data.permission ?? response.data);
}

export async function updatePermission(
  permissionId: string,
  payload: { name?: string; description?: string },
) {
  const response = await request.patch(`${AUTH_URL}/v1/admin/permissions/${permissionId}`, payload);
  return normalizePermission(response.data.permission ?? response.data);
}

export async function deletePermission(permissionId: string) {
  await request.delete(`${AUTH_URL}/v1/admin/permissions/${permissionId}`);
}

export async function deleteRole(roleId: string) {
  await request.delete(`${AUTH_URL}/v1/admin/roles/${roleId}`);
}
